import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, AlertCircle, Bot } from "lucide-react";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { AgentList as AgentListType, agentListPath } from "~/services/agents";
import { AgentCard } from "./AgentCard";

export const Agents = () => {
  const { data, error, isLoading } = useFetcher<AgentListType>(agentListPath());
  const [search, setSearch] = useState("");
  const canManageAgents = useCanManageAIAgents();
  const navigate = useNavigate();

  const headerActions = useMemo(
    () =>
      canManageAgents
        ? ([
            <Button
              key="new-agent"
              size="sm"
              className="text-xs"
              onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/new`)}
            >
              <Plus aria-hidden="true" />
              New Agent
            </Button>,
          ] as [ReactNode])
        : ([] as []),
    [canManageAgents, navigate]
  );

  useNextGenHeader({ title: "Agents", actions: headerActions });

  const filtered = (() => {
    if (!data?.items) {
      return [];
    }
    if (!search.trim()) {
      return data.items;
    }
    const lower = search.toLowerCase();
    return data.items.filter(
      (a) =>
        a.display_name.toLowerCase().includes(lower) || a.description.toLowerCase().includes(lower)
    );
  })();

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="relative w-full max-w-lg">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search agents"
          className={cn(
            "h-8 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
            "text-sm placeholder:text-muted-foreground shadow-xs",
            "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "transition-[color,box-shadow]"
          )}
        />
      </div>

      {isLoading && (
        <div
          role="status"
          aria-label="Loading agents"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
        >
          <AlertCircle className="size-8" aria-hidden="true" />
          <p className="text-sm font-medium">Failed to load agents</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Bot className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">
            {search ? "No agents match your search" : "No agents found"}
          </p>
          {search && <p className="text-xs text-muted-foreground">Try a different search term</p>}
        </div>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
          {filtered.map((agent) => (
            <li key={agent.id} className="flex flex-col">
              <AgentCard agent={agent} canManage={canManageAgents} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
